import * as Sentry from '@sentry/react';
import {FILE_UPLOAD_ACCEPTED_FORMATS, FileUpload} from '@sollapay/ui/components';
import {FILE_UPLOAD_MAX_SIZE_BYTES, formatDateTime, formatFileSize} from '@sollapay/utils';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {getTranslatedErrorMessage} from '@/utils';

import {
  useDeleteDocumentMutation,
  useDocumentsQuery,
  useDownloadDocumentMutation,
  useUploadDocumentMutation,
  useViewDocumentMutation
} from '../hooks';
import {DeleteFileModal} from '../modals';
import {matchesMetadataFilter, openDocumentPreview} from '../utils';

import type {DocumentContext, DocumentEntityType, DocumentType} from '@sollapay/enums';
import type {DocumentDto, DocumentMetadata} from '@sollapay/types';

export interface FileUploadManagerProps {
  entityType: DocumentEntityType;
  entityId: string;
  documentContext: DocumentContext;
  documentType: DocumentType;
  metadata?: DocumentMetadata;
  metadataFilter?: Record<string, string | undefined>;
  skipProcessing?: boolean;
  maxFiles?: number;
  /** Externally-owned error (e.g. a required document with nothing uploaded). Hidden while uploading; a rejected file takes precedence. */
  error?: string;
  /** When true, hides the upload dropzone and delete affordance — documents become view/download only. */
  readOnly?: boolean;
  onError?: (error: unknown) => void;
  onDocumentChange?: () => void;
  onDocumentUploaded?: (documentId: string) => void;
  onDocumentsLoaded?: (documentIds: string[]) => void;
  onUploadErrorChange?: (hasError: boolean) => void;
}

function formatUploadedAt(
  date: string | Date | null | undefined,
  justNowLabel: string,
  uploadedAtPrefix: string
): string | undefined {
  if (date == null) return undefined;
  const d = new Date(date as string | Date);
  if (isNaN(d.getTime())) return undefined;
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60_000) return justNowLabel;
  return `${uploadedAtPrefix} \u200E${formatDateTime(d)}`;
}

function toUploadedFile(doc: DocumentDto, uploadedNowLabel: string, uploadedAtLabel: string) {
  return {
    id: doc.id,
    fileName: doc.originalFileName ?? doc.fileName,
    fileSize: doc.fileSize,
    uploadedAt: formatUploadedAt(doc.uploadedAt ?? doc.createdAt, uploadedNowLabel, uploadedAtLabel)
  };
}

export function FileUploadManager({
  entityType,
  entityId,
  documentContext,
  documentType,
  metadata,
  metadataFilter,
  skipProcessing,
  maxFiles,
  error,
  readOnly = false,
  onError,
  onDocumentChange,
  onDocumentUploaded,
  onDocumentsLoaded,
  onUploadErrorChange
}: FileUploadManagerProps) {
  const {t} = useTranslation();
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [failureMessage, setFailureMessage] = useState<string>();
  const uploadedNowLabel = t('documents.uploadedNow');
  const uploadedAtLabel = t('documents.uploadedAt');

  const {data: allDocuments = []} = useDocumentsQuery(entityType, entityId, documentContext);
  const documents = allDocuments.filter(
    doc => doc.documentType === documentType && matchesMetadataFilter(doc, metadataFilter)
  );
  const onDocumentsLoadedRef = useRef(onDocumentsLoaded);
  onDocumentsLoadedRef.current = onDocumentsLoaded;

  const ids = useMemo(() => documents.map(doc => doc.id), [documents]);
  const idsKey = ids.join(',');
  useEffect(() => {
    onDocumentsLoadedRef.current?.(ids);
  }, [idsKey]);

  const uploadMutation = useUploadDocumentMutation(entityType, entityId, documentContext);
  const deleteMutation = useDeleteDocumentMutation(entityType, entityId, documentContext);
  const downloadMutation = useDownloadDocumentMutation(entityType, entityId, documentContext);
  const viewMutation = useViewDocumentMutation(entityType, entityId, documentContext);

  const reportFailure = (failure: unknown) => {
    // With onError the parent owns the display; otherwise it appears under the dropzone.
    if (onError) onError(failure);
    else setFailureMessage(getTranslatedErrorMessage(failure, t));
  };

  const handleUpload = async (file: File) => {
    setUploadProgress(0);
    setFailureMessage(undefined);
    try {
      const documentId = await uploadMutation.mutateAsync({
        file,
        documentType,
        metadata,
        skipProcessing,
        onProgress: setUploadProgress
      });
      Sentry.metrics.distribution('document.upload_size', file.size, {
        unit: 'byte',
        attributes: {documentType}
      });
      onDocumentChange?.();
      if (documentId) onDocumentUploaded?.(documentId);
    } catch (error) {
      reportFailure(error);
    } finally {
      setUploadProgress(undefined);
    }
  };

  const handleDelete = (fileId: string) => {
    setDeletingFileId(fileId);
    return Promise.resolve();
  };

  const handleConfirmDelete = async () => {
    if (deletingFileId == null) return;
    setFailureMessage(undefined);
    try {
      await deleteMutation.mutateAsync(deletingFileId);
      setDeletingFileId(null);
      onDocumentChange?.();
    } catch (error) {
      reportFailure(error);
      setDeletingFileId(null);
    }
  };

  const handleDownload = async (fileId: string) => {
    return downloadMutation.mutateAsync(fileId);
  };

  const handleView = async (fileId: string) => {
    const url = await viewMutation.mutateAsync(fileId);
    await openDocumentPreview(url);
  };

  return (
    <>
      <FileUpload
        documents={documents.map(doc => toUploadedFile(doc, uploadedNowLabel, uploadedAtLabel))}
        maxFiles={maxFiles}
        isUploading={uploadMutation.isPending}
        progress={uploadProgress}
        acceptedFormats={FILE_UPLOAD_ACCEPTED_FORMATS}
        maxFileSizeBytes={FILE_UPLOAD_MAX_SIZE_BYTES}
        clickLabel={
          maxFiles === 1 ? t('documents.clickToUploadFile') : t('documents.clickToUploadFiles')
        }
        dragLabel={t('documents.dragAndDrop')}
        maxSizeLabel={t('documents.maxSize', {size: formatFileSize(FILE_UPLOAD_MAX_SIZE_BYTES)})}
        uploadingLabel={t('documents.uploading')}
        fileTooLargeLabel={t('documents.errorFileTooLarge')}
        invalidFileTypeLabel={t('documents.errorInvalidFileType')}
        error={failureMessage ?? error}
        deleteLabel={t('documents.delete')}
        downloadLabel={t('documents.download')}
        viewLabel={t('documents.view')}
        onUpload={handleUpload}
        onDelete={handleDelete}
        onDownload={handleDownload}
        onView={handleView}
        onErrorChange={onUploadErrorChange}
        readOnly={readOnly}
      />

      <DeleteFileModal
        isOpen={deletingFileId !== null}
        onConfirm={() => void handleConfirmDelete()}
        onClose={() => setDeletingFileId(null)}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
